"use client";

import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Calendar, User, ArrowLeft, Clock } from "lucide-react";

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <div className="space-y-6">
          <div className="h-12 bg-muted/30 rounded-lg animate-pulse" />
          <div className="h-96 bg-muted/30 rounded-lg animate-pulse" />
          <div className="space-y-3">
            <div className="h-4 bg-muted/30 rounded animate-pulse" />
            <div className="h-4 bg-muted/30 rounded animate-pulse w-5/6" />
            <div className="h-4 bg-muted/30 rounded animate-pulse w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <Card className="border border-destructive/20">
          <CardContent className="py-8 text-center">
            <p className="text-destructive">Failed to load post content.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const post = posts[0];

  if (!post) {
    return (
      <div className="container mx-auto px-6 py-16 max-w-4xl">
        <Card>
          <CardContent className="py-8 text-center">
            <p className="text-muted-foreground">Post not found.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <article className="container mx-auto px-6 py-16 max-w-4xl">
      <Link 
        to="/BlogPage" 
        className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent transition-colors mb-8"
      >
        <ArrowLeft className="size-4" />
        Back to Blog
      </Link>

      <header className="mb-8 space-y-6">
        <h1 className="font-display text-5xl md:text-6xl font-bold text-foreground leading-tight">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="size-8">
                {post.author.avatar?.["48"] && (
                  <AvatarImage src={post.author.avatar["48"]} alt={post.author.name} />
                )}
                <AvatarFallback>
                  <User className="size-4" />
                </AvatarFallback>
              </Avatar>
              <span className="font-medium text-foreground">{post.author.name}</span>
            </div>
          )}

          {post.date && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-1.5">
                <Calendar className="size-4" />
                <time dateTime={post.date}>
                  {new Date(post.date).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric"
                  })}
                </time>
              </div>
            </>
          )}

          {post.content && (
            <>
              <Separator orientation="vertical" className="h-4" />
              <div className="flex items-center gap-1.5">
                <Clock className="size-4" />
                <span>{Math.ceil(post.content.split(" ").length / 200)} min read</span>
              </div>
            </>
          )}
        </div>

        {post.categories && post.categories.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {post.categories.map((category, i) => (
              <span
                key={category.id}
                data-index={i}
                className="inline-flex items-center px-3 py-1 text-xs font-semibold rounded-full bg-accent/10 text-accent"
              >
                {category.name}
              </span>
            ))}
          </div>
        )}
      </header>

      {post.featuredImage && (
        <figure className="mb-12 rounded-2xl overflow-hidden shadow-soft">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="w-full h-auto object-cover"
          />
        </figure>
      )}

      {post.content && (
        <div
          className="prose prose-lg max-w-none prose-headings:font-display prose-headings:text-foreground prose-p:text-foreground prose-a:text-accent prose-a:no-underline hover:prose-a:underline prose-strong:text-foreground prose-code:text-accent prose-pre:bg-secondary prose-pre:text-foreground-secondary"
          dangerouslySetInnerHTML={{ __html: post.content }}
        />
      )}

      {post.tags && post.tags.length > 0 && (
        <footer className="mt-12 pt-8 border-t border-border">
          <h3 className="text-sm font-semibold text-muted-foreground mb-3">Tags:</h3>
          <div className="flex flex-wrap gap-2">
            {post.tags.map((tag, i) => (
              <span
                key={tag.id}
                data-index={i}
                className="inline-flex items-center px-3 py-1 text-sm rounded-full bg-muted/30 text-foreground hover:bg-muted/50 transition-colors"
              >
                {tag.name}
              </span>
            ))}
          </div>
        </footer>
      )}

      {post.author && (
        <Card className="mt-12 bg-muted-light-background border-border">
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Avatar className="size-16">
                {post.author.avatar?.["96"] && (
                  <AvatarImage src={post.author.avatar["96"]} alt={post.author.name} />
                )}
                <AvatarFallback>
                  <User className="size-8" />
                </AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h3 className="font-display text-xl font-semibold text-foreground mb-2">
                  About {post.author.name}
                </h3>
                <p className="text-muted-foreground text-sm">
                  Contributor at Lumoria, sharing insights on architecture and design.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}
    </article>
  );
}

function RelatedPosts() {
  const { posts, loading } = usePosts();

  if (loading || !posts || posts.length === 0) {
    return null;
  }

  return (
    <section className="bg-primary py-16">
      <div className="container mx-auto px-6 max-w-6xl">
        <h2 className="font-display text-3xl md:text-4xl font-bold text-foreground text-center mb-12">
          Related Posts
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.slice(0, 3).map((post, i) => (
            <Card key={post.id} data-index={i} className="hover-lift bg-light-background border-border">
              {post.featuredImage && (
                <div className="overflow-hidden rounded-t-xl">
                  <img
                    src={post.featuredImage}
                    alt={post.title}
                    className="w-full h-48 object-cover transition-transform duration-300 hover:scale-105"
                  />
                </div>
              )}
              <CardContent className="space-y-3">
                {post.categories && post.categories.length > 0 && (
                  <span className="inline-flex items-center px-2 py-1 text-xs font-semibold rounded-full bg-accent/10 text-accent">
                    {post.categories[0].name}
                  </span>
                )}
                <h3 className="font-display text-xl font-semibold text-foreground line-clamp-2">
                  {post.title}
                </h3>
                {post.excerpt && (
                  <p className="text-sm text-muted-foreground line-clamp-3">
                    {post.excerpt.replace(/<[^>]*>/g, "")}
                  </p>
                )}
                {post.date && (
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <Calendar className="size-3" />
                    <time dateTime={post.date}>
                      {new Date(post.date).toLocaleDateString("en-US", {
                        month: "short",
                        day: "numeric",
                        year: "numeric"
                      })}
                    </time>
                  </div>
                )}
                {post.link && (
                  <Link
                    to={post.link}
                    className="inline-flex items-center gap-2 text-sm font-semibold text-accent hover:text-accent-hover transition-colors"
                  >
                    Read More
                    <ArrowLeft className="size-4 rotate-180" />
                  </Link>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function SinglePostTemplateSection() {
  const currentPostId = wvcClient.getCurrentPostID();

  return (
    <section id="single-post-template-section" className="bg-background">
      <PostsProvider postType="post" wp_query={{ include: [currentPostId], per_page: 1 }} embeds={["wp:featuredmedia", "wp:term", "author"]} fields={["id", "title.rendered", "content.rendered", "excerpt.rendered", "date", "author", "categories", "tags", "link", "_embedded", "featured_media", "meta", "_links.wp:featuredmedia", "_links.wp:term", "_links.author"]}>
        <SinglePostContent />
      </PostsProvider>

      <PostsProvider postType="post" wp_query={{ per_page: 3, exclude: [currentPostId], order: "desc", orderby: "date" }} embeds={["wp:featuredmedia", "wp:term"]} fields={["id", "title.rendered", "excerpt.rendered", "date", "categories", "link", "_embedded", "featured_media", "_links.wp:featuredmedia", "_links.wp:term"]}>
        <RelatedPosts />
      </PostsProvider>
    </section>
  );
}